import * as Sentry from '@sentry/react';
import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@ludolio/shared-ui';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@ludolio/shared-ui';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@ludolio/shared-ui';
import { Activity, Clock, TrendingUp } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { apiClient } from '@/services/api';
import type { Unit } from '@/types';
import { formatDurationFromMinutes, formatDurationFromSeconds } from '@/lib/timeFormat';

export function PlayingTimeView() {
  const [units, setUnits] = useState<Unit[]>([]);
  const [selectedUnitId, setSelectedUnitId] = useState<string>('');
  const [statistics, setStatistics] = useState<Array<{
    gameId: string;
    gameTitle: string;
    totalPlayingTimeSeconds: number;
    totalSessions: number;
    uniquePlayersCount: number;
    installationCount: number;
  }>>([]);
  const [totalStudents, setTotalStudents] = useState(0);
  const [_loading, setLoading] = useState(true);

  useEffect(() => {
    loadUnits();
  }, []);

  useEffect(() => {
    if (selectedUnitId) {
      loadStatistics();
      loadStudentCount();
    }
  }, [selectedUnitId]);

  const loadUnits = async () => {
    try {
      const response = await apiClient.getMyUnits();
      if (response.success && response.data) {
        setUnits(response.data);
        if (response.data.length > 0) {
          setSelectedUnitId(response.data[0].id);
        }
      }
    } catch (error) {
      console.error('Failed to load units:', error);
      Sentry.captureException(error);
    }
  };

  const loadStudentCount = async () => {
    try {
      const response = await apiClient.getUnitStudents(selectedUnitId);
      if (response.success && response.data) {
        // Count only enrolled students (not pending)
        const enrolled = response.data.filter(s => s.status === 'enrolled');
        setTotalStudents(enrolled.length);
      }
    } catch (error) {
      console.error('Failed to load student count:', error);
      Sentry.captureException(error);
    }
  };

  const loadStatistics = async () => {
    try {
      setLoading(true);
      const response = await apiClient.getUnitStatistics(selectedUnitId);
      if (response.success && response.data) {
        setStatistics(response.data);
      }
    } catch (error) {
      console.error('Failed to load statistics:', error);
      Sentry.captureException(error);
    } finally {
      setLoading(false);
    }
  };

  const totalPlayingSeconds = statistics.reduce((sum, stat) => sum + stat.totalPlayingTimeSeconds, 0);
  const totalPlayingMinutes = Math.round(totalPlayingSeconds / 60);
  const totalSessions = statistics.reduce((sum, stat) => sum + stat.totalSessions, 0);
  const averageSessionMinutes = totalSessions > 0 ? Math.round(totalPlayingMinutes / totalSessions) : 0;

  const playingTimeByGame = statistics.map((stat) => ({
    name: stat.gameTitle,
    totalSeconds: stat.totalPlayingTimeSeconds,
    totalHours: Number((stat.totalPlayingTimeSeconds / 3600).toFixed(2)),
    totalDuration: formatDurationFromSeconds(stat.totalPlayingTimeSeconds),
    avgSessionMinutes: stat.totalSessions > 0 ? Math.round(stat.totalPlayingTimeSeconds / 60 / stat.totalSessions) : 0,
    students: stat.uniquePlayersCount,
  }));

  const topGamesByPlaytime = [...playingTimeByGame]
    .sort((a, b) => b.totalSeconds - a.totalSeconds)
    .slice(0, 5);

  const engagementByGame = statistics.map((stat) => ({
    name: stat.gameTitle,
    engagementRate: totalStudents > 0 ? Math.min(100, Math.round((stat.uniquePlayersCount / totalStudents) * 100)) : 0,
    playedCount: stat.uniquePlayersCount,
    totalStudents,
  }));

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Playing Time</h1>
        <div className="w-full sm:w-64">
          <Select value={selectedUnitId} onValueChange={setSelectedUnitId}>
            <SelectTrigger aria-label="Select unit for playing-time data">
              <SelectValue placeholder="Select a unit" />
            </SelectTrigger>
            <SelectContent>
              {units.map((unit) => (
                <SelectItem key={unit.id} value={unit.id}>
                  {unit.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Playing Time</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatDurationFromMinutes(totalPlayingMinutes)}
            </div>
            <p className="text-xs text-muted-foreground">Selected unit total</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Average Session</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatDurationFromMinutes(averageSessionMinutes)}</div>
            <p className="text-xs text-muted-foreground">Across all sessions</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Sessions</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalSessions.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">Recorded play sessions</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="by-game">By Game</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4 mt-6">
          <div className="grid gap-4 sm:gap-6 grid-cols-1 lg:grid-cols-2">
            {/* Playing Time by Game */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base sm:text-lg">Playing Time by Game</CardTitle>
                <CardDescription className="text-xs sm:text-sm">Total hours played per game</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="w-full overflow-x-auto">
                  <ResponsiveContainer width="100%" height={300} minWidth={300}>
                    <BarChart data={playingTimeByGame}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} fontSize={10} />
                      <YAxis fontSize={10} tickFormatter={(value) => `${Math.round(Number(value))}h`} />
                      <Tooltip
                        formatter={(_value, _name, item) => [
                          item.payload?.totalDuration,
                          'Playing time',
                        ]}
                      />
                      <Bar dataKey="totalHours" fill="#3b82f6" name="Playing time" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Student Engagement Rate */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base sm:text-lg">Student Engagement Rate</CardTitle>
                <CardDescription className="text-xs sm:text-sm">% of enrolled students who played each game</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="w-full overflow-x-auto">
                  <ResponsiveContainer width="100%" height={300} minWidth={300}>
                    <BarChart data={engagementByGame}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} fontSize={10} />
                      <YAxis fontSize={10} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                      <Tooltip
                        formatter={(value, _name, item) => [
                          `${Number(value ?? 0)}% (${item.payload?.playedCount}/${item.payload?.totalStudents} students)`,
                          'Engagement',
                        ]}
                      />
                      <Bar dataKey="engagementRate" fill="#10b981" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="by-game" className="space-y-4 mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Top Games by Playing Time</CardTitle>
              <CardDescription>Games with most student engagement</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {topGamesByPlaytime.map((game, index) => (
                  <div key={game.name} className="flex items-center gap-4">
                    <div className="flex items-center gap-2 flex-1">
                      <span className="text-lg font-bold text-muted-foreground w-6">#{index + 1}</span>
                      <div className="flex-1">
                        <p className="text-sm font-medium">{game.name}</p>
                        <p className="text-xs text-muted-foreground">{game.students} students</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold">{game.totalDuration}</p>
                      <p className="text-xs text-muted-foreground">Avg session: {formatDurationFromMinutes(game.avgSessionMinutes)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>


      </Tabs>
    </div>
  );
}

